import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useSnackbar } from 'notistack';
import {
  Box,
  Button,
  Dialog,
  IconButton,
  TextField,
  Typography,
  makeStyles,
} from '@material-ui/core';
import ArrowForwardIcon from '@material-ui/icons/ArrowForward';
import CloseIcon from '@material-ui/icons/Close';
import SwapHorizIcon from '@material-ui/icons/SwapHoriz';
import { setLoading } from '../../../../actions/system.actions';
import { updateUnitNumber } from '../../../../actions/units.actions';
import { getApiErrorMessage } from '../../../../utils';

const TRANSFER_GREEN = '#2e7d32';

const useStyles = makeStyles((theme) => ({
  dialogRoot: {
    position: 'relative',
  },
  closeWrap: {
    position: 'absolute',
    right: 16,
    top: 16,
    zIndex: 1,
  },
  body: {
    textAlign: 'center',
    padding: theme.spacing(4, 8),
    [theme.breakpoints.down('xs')]: {
      padding: theme.spacing(3, 3),
    },
  },
  iconCircle: {
    width: 67,
    height: 67,
    borderRadius: '50%',
    border: `3px solid ${TRANSFER_GREEN}`,
    color: TRANSFER_GREEN,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: theme.spacing(1),
  },
  icon: {
    width: 40,
    height: 40,
  },
  title: {
    fontWeight: 'bold',
  },
  bodyText: {
    marginTop: theme.spacing(1),
  },
  transferRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexWrap: 'nowrap',
    whiteSpace: 'nowrap',
    gap: theme.spacing(1.5),
    marginTop: theme.spacing(3),
  },
  fieldBlock: {
    display: 'flex',
    alignItems: 'center',
    flexShrink: 0,
    gap: theme.spacing(1),
  },
  fieldLabel: {
    whiteSpace: 'nowrap',
    fontWeight: 500,
  },
  required: {
    color: theme.palette.error.main,
    marginRight: 2,
  },
  currentValue: {
    fontWeight: 700,
    minWidth: 64,
  },
  arrow: {
    color: TRANSFER_GREEN,
    flexShrink: 0,
  },
  newUnitInput: {
    width: 120,
    flexShrink: 0,
  },
  occupiedError: {
    color: theme.palette.error.main,
    marginTop: theme.spacing(1.5),
    lineHeight: 1.45,
  },
  actions: {
    marginTop: theme.spacing(7),
  },
  confirmButton: {
    color: '#fff',
    backgroundColor: TRANSFER_GREEN,
    '&:hover': {
      backgroundColor: '#1b5e20',
    },
  },
}));

const TransferUnitModal = ({
  open,
  onClose,
  unitId,
  currentUnitNumber,
  onSuccess,
}) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const [newUnitNumber, setNewUnitNumber] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      setNewUnitNumber('');
      setError('');
      setSubmitting(false);
    }
  }, [open]);

  const handleClose = (_event, reason) => {
    if (submitting) {
      return;
    }
    // Stay open on backdrop click; Cancel / X still close.
    if (reason === 'backdropClick') {
      return;
    }
    onClose();
  };

  const handleTransfer = async () => {
    const trimmed = String(newUnitNumber || '').trim();
    if (!trimmed) {
      setError('New unit number is required.');
      return;
    }

    setError('');
    setSubmitting(true);
    dispatch(setLoading(true));

    try {
      await dispatch(updateUnitNumber(unitId, { unit_number: trimmed }));
      enqueueSnackbar('Unit number updated successfully', {
        variant: 'success',
      });
      onClose();
      if (onSuccess) {
        await onSuccess();
      } else {
        dispatch(setLoading(false));
      }
    } catch (err) {
      const errorData = err?.response?.data || {};
      const unitNumberError = errorData.unit_number;
      const message = Array.isArray(unitNumberError)
        ? unitNumberError.join(' ')
        : unitNumberError ||
          getApiErrorMessage(errorData) ||
          'Error transferring unit.';
      setError(message);
      dispatch(setLoading(false));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      disableBackdropClick
      maxWidth="sm"
      fullWidth
      aria-labelledby="unit_transfer_dialog_title"
      PaperProps={{ id: 'unit_transfer_dialog', className: classes.dialogRoot }}
    >
      <Box className={classes.closeWrap}>
        <IconButton
          id="unit_transfer_dialog_close_button"
          aria-label="Close"
          onClick={handleClose}
          disabled={submitting}
        >
          <CloseIcon />
        </IconButton>
      </Box>
      <Box className={classes.body}>
        <Box>
          <Box className={classes.iconCircle} aria-hidden>
            <SwapHorizIcon className={classes.icon} />
          </Box>
        </Box>
        <Typography
          id="unit_transfer_dialog_title"
          variant="h6"
          color="primary"
          component="h2"
        >
          <Box className={classes.title}>Transfer Unit</Box>
        </Typography>
        <Box mt={2} />
        <Typography variant="body1" className={classes.bodyText}>
          You cannot have duplicate unit numbers. To transfer a unit, please
          make sure the new unit number is available.
        </Typography>
        <Box mt={1} />
        <Typography variant="body1" className={classes.bodyText}>
          When a unit is transferred, all tenant information, vehicle permits,
          and reserved spots remain unchanged.
        </Typography>
        <Box mt={1} />
        <Typography variant="body1" className={classes.bodyText}>
          No puede tener números de unidad duplicados. Para transferir una
          unidad, asegúrese de que el nuevo número de unidad esté disponible.
        </Typography>
        <Box mt={1} />
        <Typography variant="body1" className={classes.bodyText}>
          Cuando se transfiere una unidad, toda la información del inquilino,
          los permisos de vehículos y los espacios reservados permanecen sin
          cambios.
        </Typography>
        <Box className={classes.transferRow}>
          <Box className={classes.fieldBlock}>
            <Typography className={classes.fieldLabel}>Current unit:</Typography>
            <Typography className={classes.currentValue}>
              {currentUnitNumber || '—'}
            </Typography>
          </Box>
          <ArrowForwardIcon className={classes.arrow} fontSize="small" />
          <Box className={classes.fieldBlock}>
            <Typography className={classes.fieldLabel}>
              <Box component="span" className={classes.required}>
                *
              </Box>
              New unit:
            </Typography>
            <TextField
              id="unit_transfer_new_unit_number"
              className={classes.newUnitInput}
              size="small"
              variant="outlined"
              value={newUnitNumber}
              onChange={(e) => {
                setNewUnitNumber(e.target.value);
                if (error) {
                  setError('');
                }
              }}
              disabled={submitting}
              inputProps={{ 'aria-required': true }}
            />
          </Box>
        </Box>
        {error ? (
          <Typography
            id="unit_transfer_occupied_error"
            className={classes.occupiedError}
            role="alert"
          >
            {error}
          </Typography>
        ) : null}
        <Box className={classes.actions}>
          <Button
            id="unit_transfer_cancel_button"
            variant="outlined"
            size="large"
            color="secondary"
            onClick={handleClose}
            disabled={submitting}
          >
            Cancel
          </Button>
          <Box mr={2} component="span" />
          <Button
            id="unit_transfer_confirm_button"
            variant="contained"
            size="large"
            className={classes.confirmButton}
            onClick={handleTransfer}
            disabled={submitting}
          >
            Transfer
          </Button>
        </Box>
      </Box>
    </Dialog>
  );
};

export default TransferUnitModal;
